import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Archive, BookOpen, Mic, Sparkles,
  Heart, Users, User, LogOut, Box, Clock, Facebook, Shield, Settings, Plug, Dna,
  MessageCircle, Trophy, Brain, Utensils, Star, Search
} from "lucide-react";
import api from "@/lib/api";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/search", icon: Search, label: "Memory Search" },
  { to: "/vault", icon: Archive, label: "Vault" },
  { to: "/meals", icon: Utensils, label: "Meal Tracker" },
  { to: "/journal", icon: BookOpen, label: "Journal" },
  { to: "/voice-chat", icon: Mic, label: "Voice Chat" },
  { to: "/prompts", icon: Sparkles, label: "Prompts" },
  { to: "/timeline", icon: Clock, label: "Timeline" },
  { to: "/legacy", icon: Heart, label: "Legacy" },
  { to: "/beneficiary", icon: Users, label: "Will" },
  { to: "/connections", icon: Plug, label: "Connections" },
  { to: "/digital-dna", icon: Dna, label: "Digital DNA" },
  { to: "/knowledge-base", icon: Brain, label: "Knowledge Base" },
  { to: "/community", icon: MessageCircle, label: "Community" },
  { to: "/points", icon: Star, label: "Points & Prizes" },
  { to: "/profile", icon: User, label: "Profile" },
];

const mobileNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
  { to: "/community", icon: MessageCircle, label: "Feed" },
  { to: "/points", icon: Star, label: "Points" },
  { to: "/meals", icon: Utensils, label: "Meals" },
  { to: "/profile", icon: User, label: "Profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isSuperadmin, setIsSuperadmin] = useState(false);

  const hasAdminAccess = user?.admin_access?.length > 0;

  useEffect(() => {
    if (!user) return;
    // Check from user object directly (populated by /auth/me)
    if (user.is_superadmin === true) {
      setIsSuperadmin(true);
      return;
    }
    // Also check via dedicated endpoint (catches env-based superadmin)
    api.get("/superadmin/check").then((res) => {
      setIsSuperadmin(res.data.is_superadmin === true);
    }).catch(() => {
      setIsSuperadmin(false);
    });
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-black" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-[260px] border-r border-white/[0.06] bg-black/40 backdrop-blur-2xl p-6 justify-between shrink-0">
        <div>
          <div className="flex items-center gap-3 mb-12 px-2">
            <div className="w-9 h-9 rounded-lg bg-[#00FF88] flex items-center justify-center shadow-lg shadow-[#00FF88]/20">
              <Box className="w-5 h-5 text-black" />
            </div>
            <span className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Black Box
            </span>
          </div>
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
                      : "text-white/50 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
            {hasAdminAccess && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
                      : "text-white/50 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Shield className="w-[18px] h-[18px]" strokeWidth={1.5} />
                Admin
              </NavLink>
            )}
            {isSuperadmin && (
              <NavLink
                to="/superadmin"
                data-testid="nav-superadmin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-red-500/10 text-red-400 border border-red-500/20"
                      : "text-red-400/60 hover:text-red-400 hover:bg-red-500/5"
                  }`
                }
              >
                <Settings className="w-[18px] h-[18px]" strokeWidth={1.5} />
                Superadmin
              </NavLink>
            )}
          </nav>
        </div>
        <div className="space-y-4">
          <div className="px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08]">
            <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Signed in as</p>
            <p className="text-sm text-white truncate">{user?.name || user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/40 hover:text-red-400 hover:bg-red-500/5 w-full transition-colors duration-200"
          >
            <LogOut className="w-[18px] h-[18px]" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-2xl border-t border-white/[0.06] flex items-center justify-around px-2 py-2 z-50">
        {mobileNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-colors duration-200 ${
                isActive ? "text-[#00FF88]" : "text-white/40"
              }`
            }
          >
            <item.icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
